import { formatCount, pluralRu } from '@wit-support/ui';
import { formatFxRate } from './format.js';
/** Регион корректировок прошлых периодов ЕС (ROS) — со своим статусом подачи. */
export const EU_OSS_CORRECTIONS_REGION = 'eu-oss-corrections';
const DEFAULT_DISPLAY = {
    filingLine: null,
    listMode: 'enter_and_vat',
    layout: 'standard',
};
const REGION_DISPLAY = {
    'eu-oss': {
        filingLine: 'Как подать: вручную в ROS — по каждой стране вводите «К вводу», Греция = EL',
        listMode: 'enter_and_vat',
        layout: 'standard',
    },
    [EU_OSS_CORRECTIONS_REGION]: {
        filingLine: 'Как подать: панель корректировок ROS — страна, период, сумма НДС (минус)',
        listMode: 'correction_vat',
        layout: 'corrections',
    },
    uk: {
        filingLine: 'Как подать: через 100PcVatFreeBridge — боксы в таблице на странице региона',
        listMode: 'vat_payable',
        layout: 'standard',
    },
};
export function regionDisplay(region) {
    return REGION_DISPLAY[region] ?? DEFAULT_DISPLAY;
}
/** Строка «как подать» для карточки: своя короткая, иначе подсказка платёжки, иначе ничего. */
export function filingLineOf(region, backendHint) {
    return regionDisplay(region).filingLine ?? (backendHint.trim() ? backendHint : null);
}
// ---------------------------------------------------------------------------
// Предупреждения
// ---------------------------------------------------------------------------
/**
 * Справка о том, как считали: действий не требует. Всё остальное — «Важно»:
 * `negative_base`, `unknown_country`, `unresolved_refunds`,
 * `unsupported_currency`, `mirror_stale`, `fx_provisional`, `corrections` и
 * любой новый код платёжки (неизвестное безопаснее показать, чем спрятать).
 */
const INFO_CODES = new Set(['evidence_conflicts', 'non_renewal_excluded']);
const PAYMENTS = ['платёж', 'платежа', 'платежей'];
const REFUNDS = ['возврат', 'возврата', 'возвратов'];
/** «Румыния — 19 %» (страна разбита по смене ставки) → «Румыния». */
function stripRateSuffix(label) {
    return label.replace(/\s+—\s+[\d.,]+\s*%$/, '').trim();
}
/** Страны с отрицательной базой, по одной на страну, в порядке строк. */
export function negativeBaseCountries(rows) {
    const byCountry = new Map();
    for (const r of rows) {
        if (!r.negativeBase)
            continue;
        const key = r.country ?? r.key;
        let entry = byCountry.get(key);
        if (!entry) {
            entry = { name: stripRateSuffix(r.label) || key, rates: [], rowCount: 0 };
            byCountry.set(key, entry);
        }
        entry.rowCount += 1;
        if (r.rate !== null && r.rate !== undefined) {
            const rate = `${r.rate} %`;
            if (!entry.rates.includes(rate))
                entry.rates.push(rate);
        }
    }
    return [...byCountry.values()];
}
function joinRu(items) {
    if (items.length <= 1)
        return items.join('');
    return `${items.slice(0, -1).join(', ')} и ${items[items.length - 1]}`;
}
function negativeBaseCountryText(c) {
    if (c.rowCount <= 1) {
        return `${c.name}: возвратов больше, чем оплат — в ROS поставьте 0`;
    }
    const rates = c.rates.length > 1 ? ` по ставкам ${joinRu(c.rates)}` : '';
    return `${c.name} (все строки${rates}): возвратов больше, чем оплат — в ROS поставьте 0 по каждой`;
}
function negativeBaseText(report) {
    // Регион с боксами (UK) — одна декларация на квартал, «страны» там нет.
    if (report.boxes !== null) {
        return 'Возвратов за квартал больше, чем оплат — проверьте боксы 1 и 6 перед подачей';
    }
    const countries = negativeBaseCountries(report.rows);
    return countries.length > 0
        ? countries.map(negativeBaseCountryText).join('; ')
        : 'Возвратов больше, чем оплат — по таким странам в ROS поставьте 0';
}
/**
 * «3 платежа без страны — не вошли в расчёт». `rest` — с `{verb}`: «не вошёл»
 * для 1/21/31…, иначе «не вошли». Нет количества — «Часть платежей …».
 */
function countText(w, forms, rest) {
    if (w.count !== null && w.count > 0) {
        const verb = pluralRu(w.count, ['не вошёл', 'не вошли', 'не вошли']);
        return `${formatCount(w.count, forms)} ${rest.replace('{verb}', verb)}`;
    }
    return `Часть ${forms[2]} ${rest.replace('{verb}', 'не вошли')}`;
}
/** Своя короткая фраза по коду; неизвестный код — текст платёжки как есть. */
export function warningText(w, report) {
    switch (w.code) {
        case 'corrections':
            return 'Возвраты по продажам прошлых кварталов — внесите их в панель корректировок ROS (суммы по странам — в «Открыть таблицу»)';
        case 'non_renewal_excluded':
            return w.message;
        case 'negative_base':
            return negativeBaseText(report);
        case 'fx_provisional':
            return 'Курс предварительный — квартал ещё не закончился';
        case 'mirror_stale':
            return 'Данные Stripe давно не обновлялись — цифры могут быть неполными';
        case 'unknown_country':
            return countText(w, PAYMENTS, 'без страны — {verb} в расчёт, проверьте вручную');
        case 'unresolved_refunds':
            return countText(w, REFUNDS, 'без исходной оплаты — {verb} в расчёт');
        case 'unsupported_currency':
            return countText(w, PAYMENTS, 'не в USD — {verb} в расчёт');
        case 'evidence_conflicts':
            return w.count !== null && w.count > 0
                ? `У ${formatCount(w.count, ['платежа', 'платежей', 'платежей'])} источники страны расходятся (адрес покупателя, платёжный адрес, карта) — берём по порядку: адрес покупателя → платёжный адрес → карта, как Stripe и Taxually`
                : 'У части платежей источники страны расходятся (адрес покупателя, платёжный адрес, карта) — берём по порядку: адрес покупателя → платёжный адрес → карта, как Stripe и Taxually';
        default:
            return w.message;
    }
}
/** Делит предупреждения на «Важно» и «Справка»; неизвестный код — в «Важно». */
export function describeWarnings(report) {
    const important = [];
    const info = [];
    for (const w of report.warnings) {
        const line = { code: w.code, text: warningText(w, report) };
        if (INFO_CODES.has(w.code))
            info.push(line);
        else
            important.push(line);
    }
    return { important, info };
}
// ---------------------------------------------------------------------------
// Курсы
// ---------------------------------------------------------------------------
const PROVIDER_LABEL = {
    'ecb-quarter-end': 'ECB',
    'hmrc-monthly': 'HMRC',
};
/** `hmrc-monthly` → «HMRC»; неизвестный провайдер — как есть. */
export function fxProviderLabel(fx) {
    const providers = [...new Set(fx.map((f) => PROVIDER_LABEL[f.provider] ?? f.provider))];
    return providers.length === 1 ? (providers[0] ?? null) : null;
}
const MONTH_SHORT_RU = [
    'янв',
    'фев',
    'мар',
    'апр',
    'май',
    'июнь',
    'июль',
    'авг',
    'сен',
    'окт',
    'ноя',
    'дек',
];
/** `2026-09` → «сен»; не месяц — как есть. */
export function fxPeriodLabel(period) {
    const m = /^\d{4}-(\d{2})$/.exec(period);
    const idx = m ? Number(m[1]) - 1 : -1;
    return MONTH_SHORT_RU[idx] ?? period;
}
/** Помесячные курсы одной строкой: «июль 1,3404 · авг 1,3367 · сен 1,3554». */
/** Курс помесячный: период вида `YYYY-MM` (HMRC). */
export function isMonthlyFx(fx) {
    return fx.length > 0 && fx.every((f) => /^\d{4}-\d{2}$/.test(f.period));
}
export function fxMonthlyList(fx) {
    return fx.map((f) => `${fxPeriodLabel(f.period)} ${formatFxRate(f.rate)}`).join(' · ');
}
/**
 * На какую дату курс: `publishedFor` (`2026-09-30` → «30.09») — свой или, раз
 * в `report.fx` платёжка его не шлёт, у курса входов с тем же провайдером и
 * периодом. Нет даты: окончательный курс квартала — конец квартала из `period`
 * (`2026-Q3` → «30.09»); ПРЕДВАРИТЕЛЬНЫЙ — null: это последний доступный
 * курс, а не курс на конец квартала, и выдуманная дата ввела бы в заблуждение.
 */
export function fxAsOfLabel(f, inputsFx = []) {
    const publishedFor = f.publishedFor ??
        inputsFx.find((i) => i.provider === f.provider && i.period === f.period && i.publishedFor)
            ?.publishedFor ??
        null;
    const day = publishedFor ? /^\d{4}-(\d{2})-(\d{2})/.exec(publishedFor) : null;
    if (day)
        return `${day[2]}.${day[1]}`;
    if (f.provisional)
        return null;
    const q = /^(\d{4})-Q([1-4])$/.exec(f.period);
    if (!q)
        return null;
    const end = new Date(Date.UTC(Number(q[1]), Number(q[2]) * 3, 0));
    const dd = String(end.getUTCDate()).padStart(2, '0');
    const mm = String(end.getUTCMonth() + 1).padStart(2, '0');
    return `${dd}.${mm}`;
}
/**
 * Сводка корректировок, пришедших ВНУТРИ отчёта eu-oss (старые отчёты, до
 * выделения региона `eu-oss-corrections`); null — массива нет/он пуст или НДС
 * нулевой. Новые отчёты корректировок не несут — строки «Корректировки / Итого
 * к уплате» на карточке eu-oss тогда нет.
 */
export function correctionsSummary(report) {
    const vat = report.totals.correctionsVat;
    if (!report.corrections || report.corrections.length === 0)
        return null;
    if (vat == null || /^-?0+(\.0+)?$/.test(vat.trim()))
        return null;
    const periods = [...new Set(report.corrections.map((c) => c.period))].sort();
    return { vat, payable: report.totals.payable, periods };
}
/**
 * Отчёт рисуется как корректировки прошлых периодов: регион с таким видом
 * или (новый регион, не прописанный здесь) хоть одна строка с `period`.
 */
export function isCorrectionsReport(region, report) {
    return (regionDisplay(region).layout === 'corrections' || report.rows.some((r) => r.period !== null));
}
/** Кварталы исходных продаж строк отчёта, по порядку: `['2026-Q1', '2026-Q2']`. */
export function rowPeriods(report) {
    return [
        ...new Set(report.rows.map((r) => r.period).filter((p) => p !== null)),
    ].sort();
}
//# sourceMappingURL=region-display.js.map